import React from 'react';
import i18n from 'util/i18n';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import Subsection from 'components/Section/shared/Subsection';
import Accordion, { openState } from 'components/Form/Accordion/Accordion';
import { EmploymentCustomSummary } from 'components/Section/History/summaries';
import EmploymentItem from 'components/Section/History/Employment/EmploymentItem';
import { Gap } from 'components/Section/History/Gap';
import { getYearsString } from 'helpers/text';
import { HISTORY, HISTORY_EMPLOYMENT } from 'config/formSections/history';
import { EmptyListInstance } from 'forms/shared/models';
import Branch from 'components/Form/Branch';
import { ff } from 'helpers/featureFlags';
import Comments from 'components/Form/Comments';
import ErrorMessage from 'components/ErrorMessage';
import { getDuplicateItemsList } from 'helpers/employment';
import { sortItemsByDate } from 'util/general';
import Button from 'components/Form/Button';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { employmentAddressNameFields } from 'constants/enums/people';
import Field from 'components/Form/Field';
import { formIncludesPvqPartB, isPVQForm } from 'helpers/form';
import { DateSummary, Summary } from 'components/Summary';
import EmploymentForeignService from './EmploymentForeignService';

const sectionConfig = {
  key: HISTORY_EMPLOYMENT.key,
  section: HISTORY.name,
  store: HISTORY.store,
  subsection: HISTORY_EMPLOYMENT.name,
  storeKey: HISTORY_EMPLOYMENT.storeKey,
};

const byline = (item, index, initial, translation, required, isValid) => {
  // If item is required and not currently opened and is not valid, show message
  switch (true) {
    case required && !item.open && !isValid:
    case !item.open && !initial && item.Item && !isValid:
      return (
        <div className={`byline ${openState(item, initial)} fade in`.trim()}>
          <div className="usa-alert usa-alert-error" role="alert">
            <div className="usa-alert-body">
              <h5 className="usa-alert-heading">{i18n.m(translation)}</h5>
            </div>
          </div>
        </div>
      );
    default:
      return null;
  }
};

class Employment extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.handleError = this.handleError.bind(this);

    this.employment = React.createRef();
  }

  customEmploymentByline = (item, index, initial) => {
    const { required, errors, overrideInitial } = this.props;

    const newInitial = overrideInitial ? false : initial;
    const itemHasErrors = errors && errors.filter((e) => e.indexOf(item.uuid) > -1).length > 0;

    return byline(
      item,
      index,
      newInitial,
      'history.employment.default.collection.summary.incomplete',
      required,
      !itemHasErrors
    );
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  updateEmploymentRecordComments = (values) => {
    this.update({
      EmploymentRecordComments: values,
    });
  };

  update = (queue) => {
    this.props.onUpdate('Employment', {
      List: this.props.List,
      EmploymentRecord: this.props.EmploymentRecord,
      EmploymentRecordComments: this.props.EmploymentRecordComments,
      SummaryComments: this.props.SummaryComments,
      HasForeignService: this.props.HasForeignService, // PVQ Part B Only
      ForeignServiceList: this.props.ForeignServiceList, // PVQ Part B Only
      ...queue,
    });
  };

  updateHasForeignService = (values) => {
    this.update({
      HasForeignService: values,
      ForeignServiceList: values.value === 'Yes' ? this.props.ForeignServiceList : [],
    });
  };

  updateForeignServiceList = (values) => {
    this.update({
      ForeignServiceList: values,
    });
  };

  createNewEmploymentRecord = (values) => {
    if (values.value === 'Yes' && this.employment?.current) {
      this.employment.current.add();
      return;
    }
    this.update({
      EmploymentRecord: values,
    });
  };

  updateSort = () => {
    const { List } = this.props;
    const newList = { ...List, items: sortItemsByDate(List.items) };
    this.update({ List: newList });
  };

  sortSelector = () => (
    <Button
      classContent="default-sort"
      className="default-sort-button"
      onClick={() => this.updateSort()}
      light
      ignorePaddingMargin
      title="Sort by most recent"
    >
      <span>Sort</span>
    </Button>
  );

  foreignServiceSummary = (item, index) => {
    const obj = (item && item.Item) || {};
    const dates = DateSummary(obj.DatesServed);
    const country = (obj.Country || {}).value || '';

    return Summary({
      type: i18n.t('history.pvq.employment.foreignServiceCollection.summary.item'),
      index,
      left: country,
      right: dates,
      placeholder: i18n.t('history.pvq.employment.foreignServiceCollection.summary.unknown'),
    });
  };

  render() {
    const {
      List, totalYears, recordYears, errors, gaps, features, formErrors, peopleWhoKnowYou, formType
    } = this.props;
    const { branch, items = [] } = List;

    const duplicateItems = getDuplicateItemsList(items);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(items, peopleWhoKnowYou, employmentAddressNameFields);

    const recordYearsString = getYearsString(recordYears);
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);

    // Deprecated with EN-4733 & EN-5264
    const showGapError = !ff(features).getActive('EN-4733')
      ? branch
      && branch.value === 'No'
      && gaps.length > 0
      && formErrors.filter((e) => e.code.includes('gap.historyEmployment')).length > 0
      : false;

    const gapError = (
      <Gap
        title={i18n.t('history.employment.gap.title')}
        para={i18n.t('history.employment.gap.para', { years: totalYears })}
        gaps={gaps}
      />
    );

    const duplicateItemsError = (
      <ErrorMessage
        title="Duplicate Employment Information"
        message={[
          'Address, Employer Name, Dates, and Supervisor Name cannot be the same for the following items:',
          ...duplicateItems.map((index) => ` - Employment Item ${index + 1}`)
        ]}
      />
    );

    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return isPVQForm(formType) ? (
      <div
        className="section-content employment"
        data-section={HISTORY.key}
        data-subsection={HISTORY_EMPLOYMENT.key}
      >
        {this.sortSelector()}
        {/* summary is based on employer name but wont work for non employer field / military unemployed self employed */}
        <Accordion
          scrollToTop={this.props.scrollToTop}
          defaultState={this.props.defaultState}
          {...this.props.List}
          ref={this.employment}
          sort={this.props.sort}
          realtime={this.props.realtime}
          onUpdate={(values) => {
            this.update({ List: values });
          }}
          onError={this.handleError}
          caption={this.props.caption}
          byline={this.customEmploymentByline}
          customSummary={EmploymentCustomSummary}
          description={i18n.t('history.employment.default.collection.summary.title')}
          appendTitle={i18n.t('history.pvq.employment.default.collection.appendTitle')}
          appendLabel={i18n.t('history.pvq.employment.default.collection.append')}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          errors={accordionErrors}
          additionalErrors={
            (showGapError && gapError)
            || (duplicateItems.length > 0 && duplicateItemsError)
            || (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
            || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
          }
          ignoreInitialScrollTo
        >
          <EmploymentItem
            bind
            name="Item"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            recordYears={recordYears}
            formType={this.props.formType}
            formVersion={this.props.formVersion}
            requireEmploymentHistorySupervisorEmail={
              this.props.requireEmploymentHistorySupervisorEmail
            }
            requireEmploymentItemNonGovernment={this.props.requireEmploymentItemNonGovernment}
            requireEmploymentItemLeft={this.props.requireEmploymentItemLeft}
            selectEmploymentItemStatus={this.props.selectEmploymentItemStatus}
            selectEmploymentHistorySupervisorEmail={
              this.props.selectEmploymentHistorySupervisorEmail
            }
            requireSupervisorHasDifferentAddress={this.props.requireSupervisorHasDifferentAddress}
            requireEmploymentItemStatus={this.props.requireEmploymentItemStatus}
            requireEmploymentSelfEmployedJobLocation={
              this.props.requireEmploymentSelfEmployedJobLocation
            }
            requireSupervisorAddress={this.props.requireSupervisorAddress}
          />
        </Accordion>

        <hr className="section-divider" />

        <Branch
          attribute="EmploymentRecord"
          label={i18n.t('history.employment.default.employmentRecord.title', {
            years: recordYears,
            yearsString: recordYearsString,
          })}
          className="employment-record"
          labelSize="h4"
          {...this.props.EmploymentRecord}
          onUpdate={this.createNewEmploymentRecord}
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.m('history.employment.default.employmentRecord.list')}
          {i18n.m('history.employment.default.employmentRecord.para')}
        </Branch>

        {formIncludesPvqPartB(this.props.formType) && (
        <>
          <h1 className="section-header">{i18n.t('history.pvq.employment.summary.continuationTitle')}</h1>
          <Field optional className="no-margin-bottom">
            <p>{i18n.t('history.pvq.employment.continuationIntro.intro')}</p>
            <p>{i18n.t('history.pvq.employment.continuationIntro.introPrefix')}</p>
            <ul>
              <li>{i18n.t('history.pvq.employment.continuationIntro.introItem1')}</li>
              <li>{i18n.t('history.pvq.employment.continuationIntro.introItem2')}</li>
              <li>{i18n.t('history.pvq.employment.continuationIntro.introItem3')}</li>
            </ul>
          </Field>

          <Branch
            name="has_foreign_service"
            label={i18n.t('history.pvq.employment.heading.hasForeignService')}
            labelSize="h4"
            {...this.props.HasForeignService}
            warning
            onUpdate={this.updateHasForeignService}
            onError={this.handleError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            attribute="HasForeignService"
          />
            {this.props.HasForeignService?.value === 'Yes'
          && (
            <Accordion
              {...this.props.ForeignServiceList}
              defaultState={this.props.defaultState}
              scrollToBottom={this.props.scrollToBottom}
              onUpdate={this.updateForeignServiceList}
              onError={this.handleError}
              errors={accordionErrors}
              summary={this.foreignServiceSummary}
              description={i18n.t('history.pvq.employment.foreignServiceCollection.summary.title')}
              appendTitle={i18n.t('history.pvq.employment.foreignServiceCollection.appendTitle')}
              appendLabel={i18n.t('history.pvq.employment.foreignServiceCollection.append')}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute="ForeignServiceList"
              additionalErrors={
                (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
                || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
              }
            >
              <EmploymentForeignService
                name="Item"
                applicantBirthdate={this.props.applicantBirthdate}
                bind
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                attribute="List"
              />
            </Accordion>
          )}
        </>
        )}

      </div>
    ) : (
      <div
        className="section-content employment"
        data-section={HISTORY.key}
        data-subsection={HISTORY_EMPLOYMENT.key}
      >
        {this.sortSelector()}
        <Accordion
          scrollToTop={this.props.scrollToTop}
          defaultState={this.props.defaultState}
          {...this.props.List}
          ref={this.employment}
          sort={this.props.sort}
          realtime={this.props.realtime}
          onUpdate={(values) => {
            this.update({ List: values });
          }}
          onError={this.handleError}
          caption={this.props.caption}
          byline={this.customEmploymentByline}
          customSummary={EmploymentCustomSummary}
          description={i18n.t('history.employment.default.collection.summary.title')}
          appendTitle={i18n.t('history.employment.default.collection.appendTitle')}
          appendLabel={i18n.t('history.employment.default.collection.append')}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          errors={accordionErrors}
          additionalErrors={
            (showGapError && gapError)
            || (duplicateItems.length > 0 && duplicateItemsError)
            || (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
            || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
          }
          ignoreInitialScrollTo
        >
          <EmploymentItem
            bind
            name="Item"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            recordYears={recordYears}
            formType={this.props.formType}
            requireEmploymentHistorySupervisorEmail={
              this.props.requireEmploymentHistorySupervisorEmail
            }
            requireEmploymentItemNonGovernment={this.props.requireEmploymentItemNonGovernment}
            requireEmploymentItemLeft={this.props.requireEmploymentItemLeft}
            selectEmploymentItemStatus={this.props.selectEmploymentItemStatus}
            selectEmploymentHistorySupervisorEmail={
              this.props.selectEmploymentHistorySupervisorEmail
            }
            requireSupervisorHasDifferentAddress={this.props.requireSupervisorHasDifferentAddress}
            requireEmploymentItemStatus={this.props.requireEmploymentItemStatus}
            requireEmploymentSelfEmployedJobLocation={
              this.props.requireEmploymentSelfEmployedJobLocation
            }
            requireSupervisorAddress={this.props.requireSupervisorAddress}
          />
        </Accordion>
        <Comments
          title={i18n.t('history.employment.label.summaryComments')}
          name="Summary Comments"
          className="optional-comments"
          attribute="SummaryComments"
          {...this.props.SummaryComments}
          onUpdate={this.updateSummaryComments}
          required={false}
        />

        <hr className="section-divider" />

        <Branch
          attribute="EmploymentRecord"
          label={i18n.t('history.employment.default.employmentRecord.title', {
            years: recordYears,
            yearsString: recordYearsString,
          })}
          className="employment-record"
          labelSize="h4"
          {...this.props.EmploymentRecord}
          onUpdate={this.createNewEmploymentRecord}
          onError={this.handleError}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.m('history.employment.default.employmentRecord.list')}
          {i18n.m('history.employment.default.employmentRecord.para')}
        </Branch>
        <Comments
          title={i18n.t('history.employment.label.employmentRecordComments')}
          name="EmploymentRecordComments"
          className="optional-comments"
          attribute="EmploymentRecordComments"
          {...this.props.EmploymentRecordComments}
          onUpdate={this.updateEmploymentRecordComments}
          required={false}
        />
      </div>
    );
  }
}

Employment.defaultProps = {
  List: EmptyListInstance,
  SummaryComments: {},
  EmploymentRecord: {},
  EmploymentRecordComments: {},
  HasForeignService: {}, // PVQ Part B Only
  ForeignServiceList: {}, // PVQ Part B Only
  scrollIntoView: false,
  scrollToTop: '',
  defaultState: true,
  realtime: false,
  sort: null,
  totalYears: 10,
  recordYears: 7,
  overrideInitial: false,
  caption: null,
  onUpdate: () => { },
  onError: (value, arr) => arr,
  addressBooks: {},
  dispatch: () => { },
  gaps: [],
  errors: [],
};

export default connectSubsection(Employment, sectionConfig);

export const internalTestingHistoryEmployment = {
  Employment,
};
